import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, Save, Trash2, Wand2 } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { Modal } from "./shared";

const COLS = [["date_col", "Tarih"], ["desc_col", "Açıklama"], ["amount_col", "Tutar (+/-)"], ["debit_col", "Borç / çıkan"], ["credit_col", "Alacak / giren"], ["direction_col", "Yön (B/A)"]];

const TemplateBar = ({ templates, templateId, pick, remove }) => (
  <div className="import-toolbar">
    <label>Banka şablonu
      <select value={templateId} onChange={e => pick(e.target.value)} data-testid="import-template-select">
        <option value="">Otomatik algıla</option>
        {templates.map(t => <option key={t.id} value={t.id}>{t.bank ? `${t.bank} – ` : ""}{t.name} ({t.file_type.toUpperCase()})</option>)}
      </select>
    </label>
    {templateId && <button className="ghost-btn danger" onClick={() => remove(templateId)} data-testid="import-template-delete-button"><Trash2 size={15} /> Şablonu sil</button>}
  </div>
);

const MappingPanel = ({ data, mapping, setMapping, apply, save }) => {
  const [name, setName] = useState("");
  const [bank, setBank] = useState("");
  const set = (k, v) => setMapping({ ...mapping, [k]: v });
  const isPdf = data.file_type === "pdf";
  return (
    <div className="mapping-panel" data-testid="import-mapping-panel">
      <div className="mapping-grid">
        {!isPdf && COLS.map(([k, label]) => (
          <label key={k}>{label}
            <select value={mapping[k] || "-"} onChange={e => set(k, e.target.value)} data-testid={`mapping-${k}-select`}>
              <option value="-">— Yok —</option>
              {data.columns.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
        ))}
        {!isPdf && <label>Başlık satırı (0 = ilk satır)<input type="number" min="0" max="50" value={mapping.header_row} onChange={e => set("header_row", Number(e.target.value))} data-testid="mapping-header-row-input" /></label>}
        {isPdf && <label>Satırdaki tutar<select value={mapping.pdf_amount_pick} onChange={e => set("pdf_amount_pick", e.target.value)} data-testid="mapping-pdf-amount-pick-select"><option value="auto">Otomatik (birden çok tutarda ilkini al)</option><option value="last">Son tutar (bakiye yoksa)</option><option value="first">İlk tutar (bakiye sütunu varsa)</option></select></label>}
        {isPdf && <label>Gider anahtar kelimeleri<input placeholder="ör. borç, ödeme, B" value={mapping.pdf_expense_keywords} onChange={e => set("pdf_expense_keywords", e.target.value)} data-testid="mapping-pdf-keywords-input" /></label>}
        <label>Ondalık biçimi<select value={mapping.decimal} onChange={e => set("decimal", e.target.value)} data-testid="mapping-decimal-select"><option value="auto">Otomatik</option><option value="tr">1.234,56 (TR)</option><option value="en">1,234.56 (EN)</option></select></label>
      </div>
      <div className="mapping-actions">
        <button className="outline-btn compact" onClick={apply} data-testid="mapping-apply-button"><Wand2 size={15} /> Eşlemeyi uygula</button>
        <input placeholder="Banka adı" value={bank} onChange={e => setBank(e.target.value)} data-testid="template-bank-input" />
        <input placeholder="Şablon adı" value={name} onChange={e => setName(e.target.value)} data-testid="template-name-input" />
        <button className="outline-btn compact" disabled={!name} onClick={() => save(name, bank)} data-testid="template-save-button"><Save size={15} /> Şablon olarak kaydet</button>
      </div>
    </div>
  );
};

const ImportRows = ({ rows, setRows }) => {
  const update = (i, patch) => setRows(rows.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  return (
    <div className="import-list">
      {rows.map((row, i) => (
        <div className="import-row" key={i} data-testid={`import-row-${i}`}>
          <input type="checkbox" checked={row.selected} onChange={e => update(i, { selected: e.target.checked })} data-testid={`import-row-checkbox-${i}`} />
          <span>{row.date}</span>
          <strong title={row.title}>{row.title}</strong>
          <b className={`amount ${row.type}`}>{row.amount.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}</b>
          <select value={row.type} onChange={e => update(i, { type: e.target.value })} data-testid={`import-row-type-${i}`}><option value="expense">Gider</option><option value="income">Gelir</option></select>
          <input value={row.category} onChange={e => update(i, { category: e.target.value })} data-testid={`import-row-category-${i}`} />
        </div>
      ))}
    </div>
  );
};

export const ImportModal = ({ close, onDone, accounts }) => {
  const [file, setFile] = useState(null);
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [account, setAccount] = useState("");
  const loadTemplates = () => api.get("/import-templates").then(r => setTemplates(r.data)).catch(() => {});
  useEffect(() => { loadTemplates(); }, []);

  const run = async (f, tid, map) => {
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    if (tid) fd.append("template_id", tid);
    if (map) fd.append("mapping", JSON.stringify(map));
    try {
      const r = await api.post("/imports/preview", fd);
      setData(r.data); setRows(r.data.rows); setMapping(r.data.mapping);
      toast.success(`${r.data.count} hareket bulundu`);
    } catch (e) { toast.error(errorText(e, "Dosya okunamadı")); }
  };
  const pickFile = (e) => { const f = e.target.files[0]; setFile(f); run(f, templateId); };
  const pickTemplate = (id) => { setTemplateId(id); run(file, id); };
  const save = async (name, bank) => {
    try {
      const r = await api.post("/import-templates", { ...mapping, name, bank, file_type: data.file_type });
      toast.success("Şablon kaydedildi"); await loadTemplates(); setTemplateId(r.data.id);
    } catch (e) { toast.error(errorText(e)); }
  };
  const remove = async (id) => { await api.delete(`/import-templates/${id}`); setTemplateId(""); loadTemplates(); toast.success("Şablon silindi"); };
  const confirm = async () => {
    const r = await api.post("/imports/confirm", { rows, account });
    toast.success(`${r.data.created} hareket kaydedildi`); onDone(); close();
  };
  const selected = rows.filter(r => r.selected).length;

  return (
    <Modal title="Banka hareketlerini içe aktar" close={close} wide>
      <div className="import-flow">
        <TemplateBar templates={templates} templateId={templateId} pick={pickTemplate} remove={remove} />
        <label className="file-drop">{file ? file.name : "Dosya seçin (CSV, Excel veya PDF)"}<input type="file" accept=".csv,.xlsx,.xls,.pdf" onChange={pickFile} data-testid="bank-import-file-input" /></label>
        {data && <MappingPanel data={data} mapping={mapping} setMapping={setMapping} apply={() => run(file, "", mapping)} save={save} />}
        {data && <div className="import-hint" data-testid="import-preview-count">{rows.length} hareket bulundu, {selected} seçili. Her satırın türünü ve kategorisini kontrol edin.</div>}
        {rows.length > 0 && <>
          <ImportRows rows={rows} setRows={setRows} />
          <div className="import-footer">
            <select value={account} onChange={e => setAccount(e.target.value)} data-testid="import-account-select">
              <option value="">Banka hesabı seçin (opsiyonel)</option>
              {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
            <button className="primary-btn" onClick={confirm} disabled={!selected} data-testid="confirm-import-button">Seçilenleri kaydet <ArrowUpRight size={17} /></button>
          </div>
        </>}
      </div>
    </Modal>
  );
};
